import {
  catalogNameForChromePart,
  frameEntryForRef,
  getGlobalElements,
  getPages,
  getSurfaceCard,
  getViewFromCard,
  getWidgets,
  listChromeFrame,
  listChromeFrameRefs,
  listPageKeys,
  normalizeTheme,
  resolvePageView,
} from "../preview/document.js";
import { resolveBreakpoint } from "../contract/breakpoints.js";
import { buildTokenContext, interpolate } from "../contract/tokens.js";
import { createSpriteSheet, renderResolvedNode } from "../dom/paint.js";
import {
  getDataset,
  materializeWidgetNode,
  previewWidgetTarget,
} from "./widgets.js";

function mergeLayoutPatch(base, patch) {
  if (!patch) return base;
  const merged = { ...base, ...patch };
  if (base?.paddings || patch?.paddings) {
    merged.paddings = { ...(base?.paddings ?? {}), ...(patch?.paddings ?? {}) };
  }
  return merged;
}

function getChromeLayout(doc) {
  if (doc?.layout != null && typeof doc.layout === "object") return doc.layout;
  return null;
}

export function resolveChromeLayout(doc, breakpointName, vars) {
  const layout = getChromeLayout(doc);
  if (!layout || typeof layout !== "object") return null;
  let resolved = { ...layout };
  const patch = breakpointName ? layout.when?.[breakpointName] : null;
  resolved = mergeLayoutPatch(resolved, patch);
  return {
    maxWidth: resolved.max_width != null ? interpolate(String(resolved.max_width), vars) : null,
    paddings: resolved.paddings ?? null,
  };
}

export function documentRoot(doc, theme = "dark", surface, options = {}) {
  const resolvedSurface = surface ?? listChromeFrameRefs(doc)[0] ?? listPageKeys(doc)[0];
  const card = getSurfaceCard(doc, resolvedSurface);
  const tokenVars = buildTokenContext(doc.tokens, theme);
  let root = getViewFromCard(card);
  const catalogName = catalogNameForChromePart(doc, resolvedSurface) ?? resolvedSurface;
  const widgetSpec = getWidgets(doc)[catalogName];
  if (widgetSpec?.view) {
    const phaseCtx = {
      previewMode: options.previewMode,
      phaseByWidget: options.phaseByWidget ?? {},
    };
    const instance =
      previewWidgetTarget(catalogName, doc, phaseCtx) ?? { widget: catalogName };
    const materialized = materializeWidgetNode(instance, {
      widgets: getWidgets(doc),
      widgetName: catalogName,
      dataset: getDataset(doc),
      ...phaseCtx,
    });
    if (materialized) root = materialized;
  }
  return {
    header: card,
    root,
    vars: { ...tokenVars },
    breakpoints: doc.breakpoints ?? [],
    elements: getGlobalElements(doc),
    theme: normalizeTheme(theme),
    surface: resolvedSurface,
  };
}

function surfaceRoot(doc, surface, ctx = {}) {
  const pages = getPages(doc);
  if (Object.prototype.hasOwnProperty.call(pages, surface)) {
    return resolvePageView(doc, surface);
  }
  const catalogName = catalogNameForChromePart(doc, surface);
  if (!catalogName) return null;
  const instance = previewWidgetTarget(catalogName, doc, {
    previewMode: ctx.previewMode,
    phaseByWidget: ctx.phaseByWidget,
  }) ?? { widget: catalogName };
  return materializeWidgetNode(instance, {
    ...ctx,
    widgets: ctx.widgets ?? getWidgets(doc),
    dataset: ctx.dataset ?? getDataset(doc),
    widgetName: catalogName,
  });
}

export function mergePageVars(doc, theme = "dark", _pageKey) {
  return buildTokenContext(doc.tokens, theme);
}

function renderSurfaceFragment(doc, surface, ctx, viewportWidth) {
  const root = surfaceRoot(doc, surface, ctx);
  if (!root) return null;
  const breakpoints = ctx.breakpoints ?? doc.breakpoints ?? [];
  const surfaceCtx = {
    ...ctx,
    path: `${surface}:state`,
    breakpoint: resolveBreakpoint(breakpoints, viewportWidth),
    widgetName: catalogNameForChromePart(doc, surface) ?? ctx.widgetName,
  };
  const rendered = renderResolvedNode(root, surfaceCtx);
  if (!rendered) return null;
  rendered.classList.add("hv-page-chrome");
  rendered.dataset.card = surface;
  return rendered;
}

export function renderPageFrame(doc, options = {}) {
  const theme = normalizeTheme(options.theme ?? "dark");
  const viewportWidth = options.viewportWidth;
  const pageKey = options.pageKey && getPages(doc)[options.pageKey] ? options.pageKey : null;
  const breakpoints = doc.breakpoints ?? [];
  const elementErrors = [];
  const ctx = {
    vars: mergePageVars(doc, theme, pageKey),
    elements: getGlobalElements(doc),
    elementErrors,
    dataset: getDataset(doc),
    widgets: getWidgets(doc),
    onAction: options.onAction,
    nextId: options.nextIdStart ?? 1,
    nodes: new Map(),
    breakpoints,
    theme,
    breakpoint: resolveBreakpoint(breakpoints, viewportWidth),
    previewMode: options.previewMode,
    phaseByWidget: options.phaseByWidget ?? {},
    adminImageBaseUrl: options.adminImageBaseUrl || "",
  };

  const body = document.createElement("div");
  body.className = "hv-page-frame__body";

  const column = document.createElement("div");
  column.className = "hv-page-frame__main";
  column.style.width = "100%";
  column.style.boxSizing = "border-box";
  column.style.display = "flex";
  column.style.flexDirection = "column";
  column.style.gap = "0";

  let header = null;
  let footer = null;
  let sidebar = null;
  const rest = [];
  for (const entry of listChromeFrame(doc)) {
    const ref = entry?.ref;
    if (!ref) continue;
    const rendered = renderSurfaceFragment(doc, ref, ctx, viewportWidth);
    if (!rendered) continue;
    if (ref === "sidebar" || ref === "app_sidebar") sidebar = rendered;
    else if (ref === "header" || ref === "app_header") header = rendered;
    else if (ref === "footer" || ref === "app_footer") footer = rendered;
    else rest.push(rendered);
  }

  const breakpointName = resolveBreakpoint(breakpoints, viewportWidth);
  const sidebarBeside = sidebar && (breakpointName === "wide" || breakpointName === "regular" || breakpointName === "compact");
  if (sidebarBeside) {
    sidebar.style.width = breakpointName === "wide" ? "280px" : "64px";
    sidebar.style.flex = "0 0 auto";
    sidebar.style.alignSelf = "stretch";
    body.append(sidebar);
  }
  if (header) column.append(header);
  for (const rendered of rest) column.append(rendered);
  if (pageKey) {
    const rendered = renderSurfaceFragment(doc, pageKey, ctx, viewportWidth);
    if (rendered) column.append(rendered);
  }
  if (sidebar && !sidebarBeside) {
    sidebar.style.height = "max-content";
    sidebar.style.minHeight = "0";
    column.append(sidebar);
  }
  if (footer) column.append(footer);
  body.append(column);

  return {
    el: body,
    nodes: ctx.nodes,
    count: ctx.nodes.size,
    breakpoint: ctx.breakpoint,
    elementErrors,
    nextId: ctx.nextId,
  };
}

export function renderDocument(doc, options = {}) {
  const theme = normalizeTheme(options.theme ?? "dark");
  const surface = options.surface ?? listChromeFrameRefs(doc)[0] ?? listPageKeys(doc)[0];
  const { root, vars, breakpoints } = documentRoot(doc, theme, surface, {
    previewMode: options.previewMode,
    phaseByWidget: options.phaseByWidget ?? {},
  });
  const breakpoint = resolveBreakpoint(breakpoints, options.viewportWidth);
  const elementErrors = [];
  const ctx = {
    vars,
    elements: getGlobalElements(doc),
    elementErrors,
    dataset: getDataset(doc),
    widgets: getWidgets(doc),
    onAction: options.onAction,
    nextId: 1,
    nodes: new Map(),
    path: "state",
    breakpoint,
    previewMode: options.previewMode,
    phaseByWidget: options.phaseByWidget ?? {},
    widgetName: listChromeFrameRefs(doc).includes(surface) ? surface : undefined,
    adminImageBaseUrl: options.adminImageBaseUrl || "",
  };
  if (!root) throw new Error("В JSON нет view");
  const wrap = document.createDocumentFragment();
  if (options.includeSprite !== false) wrap.append(createSpriteSheet());
  const rendered = renderResolvedNode(root, ctx);
  if (rendered) wrap.append(rendered);
  return {
    el: wrap,
    nodes: ctx.nodes,
    count: ctx.nodes.size,
    breakpoint,
    elementErrors,
  };
}
